import { useCallback } from 'react';
import { notesAPI } from '@/lib/api';
import { encryptContent } from '@/lib/crypto';
import { processTemplateVariables } from '@/lib/templateVariables';
import * as offlineDB from '@/lib/offlineDB';
import { toast } from 'sonner';

/**
 * Note CRUD operations, archiving, pinning, favoriting, sharing.
 */
export function useNoteActions({
  notes, setNotes, selectedNoteId, setSelectedNoteId,
  decryptNote, isEncryptionEnabled, encryptionKey, user,
  notebooks, templates, setPendingSyncCount, sessionIdRef,
}) {
  const createNote = useCallback(async (data = {}) => {
    try {
      let noteData = { title: '', content: '', ...data };

      // Auto-apply default template from notebook
      if (noteData.notebook_id && !noteData.content) {
        const nb = notebooks.find((n) => n.id === noteData.notebook_id);
        if (nb?.default_template_id) {
          const tmpl = templates.find((t) => t.id === nb.default_template_id);
          if (tmpl?.content) {
            noteData.content = processTemplateVariables(tmpl.content, {
              title: noteData.title || 'Untitled',
              user: user?.email || '',
            });
          }
        }
      }

      if (isEncryptionEnabled && encryptionKey && noteData.content) {
        const encrypted = await encryptContent(noteData.content, encryptionKey);
        noteData = { ...noteData, content: encrypted.content, is_encrypted: true, encryption_iv: encrypted.iv, encryption_salt: user?.encryption_salt };
      }

      // If offline, save locally and queue for sync
      if (!navigator.onLine) {
        const offlineId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const offlineNote = {
          ...noteData,
          id: offlineId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_offline: true,
        };
        await offlineDB.saveNote(offlineNote);
        await offlineDB.addToPendingSync({ type: 'CREATE_NOTE', data: noteData, offlineId });
        setPendingSyncCount((prev) => prev + 1);
        setNotes((prev) => [offlineNote, ...prev]);
        setSelectedNoteId(offlineId);
        toast.info('Note saved offline. Will sync when online.');
        return offlineNote;
      }

      const res = await notesAPI.create(noteData);
      const newNote = await decryptNote(res.data);
      setNotes((prev) => [newNote, ...prev]);
      setSelectedNoteId(newNote.id);
      await offlineDB.saveNote(newNote);
      return newNote;
    } catch (err) {
      toast.error('Failed to create note');
      throw err;
    }
  }, [isEncryptionEnabled, encryptionKey, decryptNote, notebooks, templates, user?.email, user?.encryption_salt, setNotes, setSelectedNoteId, setPendingSyncCount]);

  const updateNote = useCallback(async (noteId, data) => {
    try {
      const existingNote = notes.find((n) => n.id === noteId);
      if (existingNote && (existingNote._decryptFailed || existingNote._needsDecryption)) {
        console.warn('[UpdateNote] Skipping save for note with failed/pending decryption:', noteId);
        return existingNote;
      }

      let noteData = { ...data };

      if (existingNote?.is_published && 'content' in noteData && noteData.content) {
        noteData.published_content = noteData.content;
      }

      if (isEncryptionEnabled && encryptionKey && 'content' in noteData && noteData.content) {
        const encrypted = await encryptContent(noteData.content, encryptionKey);
        noteData = { ...noteData, content: encrypted.content, is_encrypted: true, encryption_iv: encrypted.iv, encryption_salt: user?.encryption_salt };
      }

      if (!navigator.onLine) {
        const updatedNote = { ...existingNote, ...noteData, updated_at: new Date().toISOString(), is_offline: true };
        await offlineDB.saveNote(updatedNote);
        if (!noteId.startsWith('offline_')) {
          await offlineDB.addToPendingSync({ type: 'UPDATE_NOTE', noteId, data: noteData });
          setPendingSyncCount((prev) => prev + 1);
        }
        setNotes((prev) => prev.map((n) => (n.id === noteId ? updatedNote : n)));
        return updatedNote;
      }

      const sessionId = sessionIdRef?.current || '';
      const res = await notesAPI.update(noteId, noteData, sessionId);
      const updated = await decryptNote(res.data);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      await offlineDB.saveNote(updated);
      return updated;
    } catch (err) {
      if (!navigator.onLine || err.message?.includes('Network Error')) {
        const existingNote = notes.find((n) => n.id === noteId);
        const updatedNote = { ...existingNote, ...data, updated_at: new Date().toISOString(), is_offline: true };
        await offlineDB.saveNote(updatedNote);
        await offlineDB.addToPendingSync({ type: 'UPDATE_NOTE', noteId, data });
        setPendingSyncCount((prev) => prev + 1);
        setNotes((prev) => prev.map((n) => (n.id === noteId ? updatedNote : n)));
        toast.info('Changes saved offline. Will sync when online.');
        return updatedNote;
      }
      toast.error('Failed to save note');
      throw err;
    }
  }, [isEncryptionEnabled, encryptionKey, decryptNote, notes, user?.encryption_salt, setNotes, setPendingSyncCount]);

  const deleteNote = useCallback(async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, is_deleted: true } : n)));
      if (selectedNoteId === noteId) setSelectedNoteId(null);
      toast.success('Note moved to trash');
    } catch { toast.error('Failed to delete note'); }
  }, [selectedNoteId, setNotes, setSelectedNoteId]);

  const permanentDeleteNote = useCallback(async (noteId) => {
    try {
      await notesAPI.permanentDelete(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      if (selectedNoteId === noteId) setSelectedNoteId(null);
      toast.success('Note permanently deleted');
    } catch { toast.error('Failed to delete note'); }
  }, [selectedNoteId, setNotes, setSelectedNoteId]);

  const emptyTrash = useCallback(async () => {
    try {
      const res = await notesAPI.emptyTrash();
      const count = res.data.deleted || 0;
      setNotes((prev) => prev.filter((n) => !n.is_deleted));
      setSelectedNoteId(null);
      toast.success(`Trash emptied — ${count} note${count !== 1 ? 's' : ''} deleted`);
    } catch { toast.error('Failed to empty trash'); }
  }, [setNotes, setSelectedNoteId]);

  const archiveNote = useCallback(async (noteId) => {
    try {
      await notesAPI.archive(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, is_archived: true } : n)));
      toast.success('Note archived');
    } catch { toast.error('Failed to archive note'); }
  }, [setNotes]);

  const restoreNote = useCallback(async (noteId) => {
    try {
      await notesAPI.restore(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, is_archived: false, is_deleted: false } : n)));
      toast.success('Note restored');
    } catch { toast.error('Failed to restore note'); }
  }, [setNotes]);

  const togglePin = useCallback(async (noteId) => {
    try {
      const res = await notesAPI.pin(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, is_pinned: res.data.is_pinned } : n)));
    } catch { toast.error('Failed to pin note'); }
  }, [setNotes]);

  const toggleFavorite = useCallback(async (noteId) => {
    try {
      const res = await notesAPI.favorite(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, is_favorited: res.data.is_favorited } : n)));
    } catch { toast.error('Failed to favorite note'); }
  }, [setNotes]);

  const shareNote = useCallback(async (noteId) => {
    try {
      const res = await notesAPI.share(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, share_token: res.data.share_token, is_shared: true } : n)));
      toast.success('Share link created');
      return res.data.share_token;
    } catch { toast.error('Failed to share note'); }
  }, [setNotes]);

  const unshareNote = useCallback(async (noteId) => {
    try {
      await notesAPI.unshare(noteId);
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, share_token: null, is_shared: false } : n)));
      toast.success('Share link removed');
    } catch { toast.error('Failed to unshare note'); }
  }, [setNotes]);

  const updateNoteLocal = useCallback((noteId, fields) => {
    setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, ...fields } : n)));
  }, [setNotes]);

  return {
    createNote, updateNote, deleteNote, permanentDeleteNote, emptyTrash,
    archiveNote, restoreNote, togglePin, toggleFavorite,
    shareNote, unshareNote, updateNoteLocal,
  };
}
